import { useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Snackbar,
  TextField,
} from '@mui/material'
import axios from 'axios'
import { useUser } from '../hooks'
import { ownerFilterQueryParam } from '../helper'
import { quoteLineType, quoteType } from '../types'
import './DistributorAddToQuote.css'

type Props<T> = {
  open: boolean
  onClose: () => void
  partNumbers: string[]
  results: Record<string, T | null>
  itemToLineItem: (lineNumber: number, mfgPN: string, item: T) => Partial<quoteLineType>
}

function buildSuccessMessage(addedParts: string[], totalFound: number) {
  if (addedParts.length === totalFound) return 'All parts added to quote.'
  if (addedParts.length === 1) return `${addedParts[0]} added to quote.`
  return `${addedParts.length} parts added to quote.`
}

export default function DistributorAddToQuoteDialog<T>(props: Props<T>) {
  const user = useUser()
  const [quotes, setQuotes] = useState<quoteType[]>([])
  const [selectedQuote, setSelectedQuote] = useState<quoteType | null>(null)
  const [checkedParts, setCheckedParts] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string[] | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [addedQuoteId, setAddedQuoteId] = useState<string | null>(null)
  const [addedLineItems, setAddedLineItems] = useState<{ lineItemId: string; lineNumber: number }[]>([])

  useEffect(() => {
    if (!props.open || !user.email) return
    axios
      .get('/quote?quoteNumber', {
        params: {
          pageSize: 1000,
          sort: 'CreatedDate',
          sortDirection: 'desc',
          ownerFilter: ownerFilterQueryParam(user.email, user.role),
        },
      })
      .then(res => setQuotes(res.data.list))
      .catch(err => console.error('Failed to load quotes', err))
  }, [props.open, user.email, user.role.admin, user.role.operations])

  useEffect(() => {
    if (!props.open) {
      setSelectedQuote(null)
      setCheckedParts([])
      setSubmitError(null)
    }
  }, [props.open])

  const allChecked = props.partNumbers.length > 0 && checkedParts.length === props.partNumbers.length

  const toggleAll = () => setCheckedParts(allChecked ? [] : [...props.partNumbers])
  const togglePart = (pn: string) =>
    setCheckedParts(prev => (prev.includes(pn) ? prev.filter(p => p !== pn) : [...prev, pn]))

  const handleSubmit = async () => {
    if (!selectedQuote || checkedParts.length === 0) return
    setSubmitting(true)
    setSubmitError(null)
    const partsToAdd = checkedParts
    const quoteId = selectedQuote.QuoteId

    try {
      const quoteRes = await axios.get(`/quote/${quoteId}`)
      let nextLineNumber = quoteRes.data.lineItems.filter((li: quoteLineType) => li.LineNumber !== -1).length + 1
      const failed: string[] = []
      const succeededParts: string[] = []
      const added: { lineItemId: string; lineNumber: number }[] = []
      for (const pn of partsToAdd) {
        const item = props.results[pn]
        if (!item) continue
        try {
          const lineItem = { ...props.itemToLineItem(nextLineNumber, pn, item), QuoteId: quoteId }
          await axios.post(`/quote/${quoteId}/lineItem`, { lineItem })
          added.push({ lineItemId: lineItem.LineItemId as string, lineNumber: nextLineNumber })
          succeededParts.push(pn)
          nextLineNumber += 1
        } catch (err) {
          console.error(`Failed to add ${pn} to quote`, err)
          failed.push(pn)
        }
      }
      if (added.length > 0) {
        setAddedQuoteId(quoteId)
        setAddedLineItems(added)
        setSuccessMessage(buildSuccessMessage(succeededParts, props.partNumbers.length))
      }
      if (failed.length === 0) {
        props.onClose()
      } else {
        setCheckedParts(failed)
        setSubmitError(failed)
      }
    } catch (err) {
      console.error('Failed to load quote before adding line items', err)
      setSubmitError(partsToAdd)
    } finally {
      setSubmitting(false)
    }
  }

  const handleUndo = async () => {
    if (!addedQuoteId) return
    const remaining: { lineItemId: string; lineNumber: number }[] = []
    // Delete from the highest line number down — deleting shifts every later line number down by one,
    // so going bottom-up would make the next delete target the wrong line... I think.
    const byLineNumberDesc = [...addedLineItems].sort((a, b) => b.lineNumber - a.lineNumber)
    for (const { lineItemId, lineNumber } of byLineNumberDesc) {
      try {
        await axios.delete(`/quote/${addedQuoteId}/lineItem/${lineItemId}`, { data: { lineNumber } })
      } catch (err) {
        console.error(`Failed to undo line item ${lineItemId}`, err)
        remaining.push({ lineItemId, lineNumber })
      }
    }
    setAddedLineItems(remaining)
    if (remaining.length > 0) {
      setSuccessMessage(`Failed to undo ${remaining.length} item(s). Click Undo to retry.`)
    } else {
      setSuccessMessage(null)
      setAddedQuoteId(null)
    }
  }

  return (
    <>
      <Dialog open={props.open} onClose={submitting ? undefined : props.onClose} fullWidth maxWidth="sm">
        <DialogTitle>Please select the quote and part numbers you would like to add to the quote.</DialogTitle>
        <DialogContent className="distributor-add-to-quote-dialog-content">
          <Autocomplete
            fullWidth
            options={quotes}
            getOptionLabel={q => `${q.QuoteNumber}v${q.Version}${q.Name ? ' - ' + q.Name : ''}`}
            isOptionEqualToValue={(a, b) => a.QuoteId === b.QuoteId}
            value={selectedQuote}
            onChange={(_e, value) => setSelectedQuote(value)}
            renderInput={params => <TextField {...params} label="Quote" />}
          />
          <FormGroup>
            <FormLabel>Part Numbers</FormLabel>
            <FormControlLabel control={<Checkbox checked={allChecked} onChange={toggleAll} />} label="Select All" />
            {props.partNumbers.map(pn => (
              <FormControlLabel
                key={pn}
                control={<Checkbox checked={checkedParts.includes(pn)} onChange={() => togglePart(pn)} />}
                label={pn}
              />
            ))}
          </FormGroup>
          {submitError && <Alert severity="error">Failed to add: {submitError.join(', ')}. Please try again.</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose} variant="outlined" disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!selectedQuote || checkedParts.length === 0 || submitting}
            loading={submitting}
          >
            Add To Quote
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        autoHideDuration={5000}
        open={successMessage !== null}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
        action={
          <Button color="primary" size="small" onClick={handleUndo}>
            Undo
          </Button>
        }
      />
    </>
  )
}
